import type { ErrorCode, OutputFormat } from '../types'

export const MAX_FILE_SIZE_BYTES = 30 * 1024 * 1024
export const MAX_QUEUE_ITEMS = 20
export const MAX_PIXEL_LONGEST_SIDE = 12000

export const ALLOWED_EXTENSIONS = new Set(['.heic', '.heif'])

export function isHeicFileName(fileName: string): boolean {
  const ext = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase()
  return ext === 'heic' || ext === 'heif'
}

/** @deprecated use isHeicFileName */
export const isHeicCandidate = isHeicFileName

export function exceedsImageLimits(width: number, height: number): boolean {
  return width > MAX_PIXEL_LONGEST_SIDE
    || height > MAX_PIXEL_LONGEST_SIDE
}

export function formatBytes(bytes: number, decimals = 1): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const value = bytes / k ** i
  return `${value.toFixed(decimals).replace(/\.0+$/, '')} ${sizes[i]}`
}

export interface ValidationError {
  fileName: string
  code: ErrorCode
  reason: string
}

export function validateFiles(
  files: FileList | readonly File[] | null,
  existingCount: number,
): { accepted: File[]; rejected: ValidationError[] } {
  const accepted: File[] = []
  const rejected: ValidationError[] = []
  if (!files || files.length === 0) {
    return { accepted, rejected }
  }
  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    if (existingCount + accepted.length >= MAX_QUEUE_ITEMS) {
      rejected.push({
        fileName: file.name,
        code: 'too-many-files',
        reason: 'You can add up to 20 files at a time.',
      })
      continue
    }
    if (!isHeicFileName(file.name)) {
      rejected.push({
        fileName: file.name,
        code: 'unsupported-container-or-codec',
        reason: 'This file does not look like a HEIC or HEIF file.',
      })
      continue
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      rejected.push({
        fileName: file.name,
        code: 'file-too-large',
        reason: 'This file is larger than 30 MB.',
      })
      continue
    }
    accepted.push(file)
  }
  return { accepted, rejected }
}

export function mimeTypeFor(format: OutputFormat): string {
  switch (format) {
    case 'jpg':
      return 'image/jpeg'
    case 'png':
      return 'image/png'
    case 'pdf':
      return 'application/pdf'
    default:
      return 'application/octet-stream'
  }
}

export function extensionFor(format: OutputFormat): string {
  switch (format) {
    case 'jpg':
      return 'jpg'
    case 'png':
      return 'png'
    case 'pdf':
      return 'pdf'
    default:
      return 'bin'
  }
}
